/* eslint-disable @typescript-eslint/no-misused-promises */
import React, { useEffect, useMemo } from 'react'
import { Box, CircularProgress, Divider, Grid, Typography, useTheme } from '@mui/material'
import { FormProvider, type SubmitHandler, useForm } from 'react-hook-form'
import { yupResolver } from '@hookform/resolvers/yup'
import DefaultModalActions from '../../../components/GenericModal/DefaultModalActions'
import { ButtonType } from '../../../components/GenericButton/styles'
import FormInput from '../../../components/FormElements/FormInput'
import FormSwitch from '../../../components/FormElements/FormSwitch'
import { useMutation, useQuery } from '@tanstack/react-query'
import { AutoImportManager } from '../../../APIManager'
import { notification } from '../../../components/Notifications'
import { SlotStatus, type ISlot } from '../../../models/DistributionPlan'
import { MultiChipApiAutoComplete } from '../../../components/FormElements/MultiChipApiAutoComplete'
import { type AxiosResponse, type AxiosError } from 'axios'
import { getSlotApi, getSlotSectionApi } from '../../../store/slices/distributionPlanSlice/apis'
import { useAppDispatch } from '../../../store'
import { useParams } from 'react-router-dom'
import { surveyRulesSchema } from './constant'
import { type ICourseFilters } from '../../SurveyTempleteFeature/components/FiltersModal'
import AttributesFilter from '../../SurveyTempleteFeature/components/FiltersModal/AttributesFilter'

interface SyncRulesPanelProps {
  handleCloseModal: () => void
  slot: ISlot
}

interface FormType {
  isSyncAllSectionsForTermActive?: boolean
  isReportingHierarchyFilterActive?: boolean
  isCourseAttributeFilterActive?: boolean
  academicUnits?: Array<{ divisionId: string }>
  courseFilters?: ICourseFilters[]
  minimumEnrollment?: number | null
}

const SyncRulesPanel: React.FC<SyncRulesPanelProps> = ({ handleCloseModal, slot }) => {
  const { id } = useParams()
  const theme = useTheme()
  const dispatch = useAppDispatch()

  const { data: slotData, isLoading: syncRulesLoading, isFetching: syncRulesFetching } = useQuery({
    queryKey: ['slotData', slot.slotId],
    queryFn: async () => await AutoImportManager.getSlotById(slot.slotId),
    enabled: !!slot?.slotId
  })

  const methods = useForm<any>({ resolver: yupResolver(surveyRulesSchema), defaultValues: {} })

  const { handleSubmit, control, watch, reset, setValue, formState: { isDirty } } = methods

  const { mutate, isPending: isPendingAutoImport } = useMutation({
    mutationFn: AutoImportManager.submitAutoImport,
    onSuccess: (res: AxiosResponse) => {
      notification(res?.data?.status ?? 'Successfully Updated', { variant: 'success' })
      void dispatch(getSlotApi({ urlId: id }))
      void dispatch(getSlotSectionApi({ urlId: id }))
      handleCloseModal()
    },
    onError: (error: AxiosError) => {
      const errorMessage = error.message
      let messageToShow = 'Bad request. Please check the data and try again'
      if (typeof errorMessage === 'string') {
        messageToShow = errorMessage
      } else if (errorMessage && typeof errorMessage === 'object') {
        const firstKey = Object.keys(errorMessage)[0]
        if (firstKey) {
          messageToShow = errorMessage[firstKey]
        }
      }

      notification(messageToShow, { variant: 'error' })
    }
  })

  const onSubmit: SubmitHandler<FormType> = async (values) => {
    const { isSyncAllSectionsForTermActive, isCourseAttributeFilterActive, isReportingHierarchyFilterActive, minimumEnrollment, academicUnits, courseFilters } = values
    if (isCourseAttributeFilterActive && courseFilters?.length === 0) {
      notification('At least one Course Attributes filter is required', { variant: 'error' })
      return
    }

    const preparedAttributes = courseFilters?.map(({ op, value, key }: ICourseFilters) => ({
      op,
      values: value,
      type: key?.attributeType,
      key: key?.attributeKey
    }))

    const payload = {
      isSyncAllSectionsForTermActive,
      isCourseAttributeFilterActive: isSyncAllSectionsForTermActive ? false : isCourseAttributeFilterActive,
      isReportingHierarchyFilterActive: isSyncAllSectionsForTermActive ? false : isReportingHierarchyFilterActive,
      minimumEnrollment,
      academicUnits: isReportingHierarchyFilterActive && !isSyncAllSectionsForTermActive ? academicUnits?.map(({ divisionId }) => divisionId) : undefined,
      attributes: isCourseAttributeFilterActive && !isSyncAllSectionsForTermActive ? preparedAttributes : undefined
    }

    mutate({ slotId: slot.slotId, data: payload })
  }

  const slotDataMemo = useMemo(() => slotData, [slotData])

  useEffect(() => {
    if (slotDataMemo) {
      reset({
        isSyncAllSectionsForTermActive: slotDataMemo.isSyncAllSectionsForTermActive ?? false,
        isReportingHierarchyFilterActive: slotDataMemo.isReportingHierarchyFilterActive ?? false,
        isCourseAttributeFilterActive: slotDataMemo.isCourseAttributeFilterActive ?? false,
        minimumEnrollment: slotDataMemo.minimumEnrollment,
        academicUnits: slotDataMemo?.academicUnits ?? []
      })
    }
  }, [slotDataMemo, reset])

  const isReportingHierarchyFilterActive = watch('isReportingHierarchyFilterActive')
  const isCourseAttributeFilterActive = watch('isCourseAttributeFilterActive')
  const isSyncAllSectionsForTermActive = watch('isSyncAllSectionsForTermActive')

  useEffect(() => {
    if (!isCourseAttributeFilterActive) {
      setValue('courseFilters', [])
    }
  }, [isCourseAttributeFilterActive])

  const slotStatus = slot?.slotNodeDetails?.slotStatus
  const isButtonDisabled = slotStatus !== SlotStatus.Draft
  return (
    <FormProvider {...methods}>
      <form onSubmit={handleSubmit(onSubmit)}>

        {
          syncRulesLoading || syncRulesFetching

            ? <Box display="flex" justifyContent="center" alignItems="center" minHeight={theme.spacing(100)}>
              <CircularProgress />
            </Box>

            : <>
              <FormSwitch
                name="isSyncAllSectionsForTermActive"
                control={control}
                label="Sync All Course Sections from Term"
                checked={isSyncAllSectionsForTermActive}
                disabled={isButtonDisabled}
              />
              <Typography color="#000" sx={{ fontSize: '14px', marginTop: 2 }}>
                Sync all Course Sections that fall under the linked Term.
              </Typography>

              <Divider sx={{ mt: 4, mb: 5 }} />
              <FormSwitch
                name="isReportingHierarchyFilterActive"
                control={control}
                label="Reporting Hierarchy"
                disabled={isSyncAllSectionsForTermActive || isButtonDisabled }
                checked={isReportingHierarchyFilterActive && !isSyncAllSectionsForTermActive}
              />
              <Typography color="#000" sx={{ fontSize: '14px', marginTop: 2 }}>
                Sync Course Sections from this term that fall under the following Academic Units.
              </Typography>

              {isReportingHierarchyFilterActive && !isSyncAllSectionsForTermActive && (
                <Grid item mt={3} xs={12}>
                  <MultiChipApiAutoComplete
                    apiEndpoint="/api/divisions/"
                    label='Parent Unit'
                    placeholder="Select Parent Unit"
                    name="academicUnits"
                    valueField="divisionId"
                    labelField="title"
                    control={control}
                    disabled={isButtonDisabled}
                    required={isReportingHierarchyFilterActive}
                  />
                </Grid>
              )}

              <Divider sx={{ mt: 4, mb: 5 }} />
              <FormSwitch
                name="isCourseAttributeFilterActive"
                control={control}
                label="Course Attributes"
                disabled={isSyncAllSectionsForTermActive || isButtonDisabled}
                checked={isCourseAttributeFilterActive && !isSyncAllSectionsForTermActive}
              />
              <Typography color="#000" sx={{ fontSize: '14px', marginTop: 2 }}>
                Sync Course Sections from this term that have the following Attributes.
              </Typography>

              {isCourseAttributeFilterActive && !isSyncAllSectionsForTermActive && (
                <AttributesFilter attributes={slotData?.attributes ?? []} initialLabel="Sync if" />
              )}

              <Divider sx={{ mt: 4, mb: 5 }} />
              <Box>
                <Typography color="#000" sx={{ fontWeight: 600, fontSize: '16px' }}>
                  Minimum Course Enrollment
                </Typography>
                <Typography color="#000" sx={{ fontSize: '14px', marginTop: 2 }}>
                  Minimum enrollment required for a course section survey to be available.
                </Typography>
                <Box mt={4}>
                  <FormInput disabled={isButtonDisabled} name="minimumEnrollment" placeholder="Set Minimum Enrollment"/>
                </Box>
              </Box>
            </>
        }

        <DefaultModalActions
          confirmButtonProps={{
            type: 'submit',
            text: 'Submit',
            buttonType: ButtonType.Primary,
            disabled: !isDirty || isPendingAutoImport || isButtonDisabled,
            loading: isPendingAutoImport
          }}
          destructiveButtonProps={{
            onClick: handleCloseModal
          }}
        />
      </form>
    </FormProvider>
  )
}

export default SyncRulesPanel
